import { useMemo, type ReactElement } from 'react'
import { isEmpty, type DashboardWidget, type ReportColumnValue, type TabularResult } from '@terros/common'
import { Group, Text } from '@mantine/core'
import {
  getReportGroupEntityId,
  getReportGroupEntityName,
  getSelectedReportColumnDisplayLabel,
  getWidgetGrouping,
  toReportGroupEntity,
  useReportColumnsMetadata,
  type ReportGroupEntity,
} from '@front/model'
import { DisplayReportColumnValue, TDataTable, type TDataTableColumn } from '@components'

type Props = {
  widget: Extract<DashboardWidget, { type: 'table' }>
  data: TabularResult
}

type TableRow = {
  entity: ReportGroupEntity
  metrics: ReportColumnValue[]
}

const MULTI_METRIC_COLUMN_WIDTH = 180

export function TableWidget({ data, widget }: Props): ReactElement {
  const { columnsMap: reportColumnsMap, variantMap } = useReportColumnsMetadata()
  const grouping = getWidgetGrouping(widget)
  const selectedColumns = useMemo(() => new Set(widget.metrics.map((metric) => metric.kpi)), [widget.metrics])

  const records = useMemo((): TableRow[] => {
    if (isEmpty(data.rows)) return []
    return data.rows
      .map((row): TableRow | undefined => {
        const entity = toReportGroupEntity(row[0])
        if (!entity) return undefined
        const metrics = widget.metrics.map((_, index) => row[index + 1] ?? null)
        return { entity, metrics }
      })
      .filter((row): row is TableRow => row !== undefined)
  }, [data.rows, widget.metrics])

  const columns = useMemo((): TDataTableColumn<TableRow>[] => {
    const hasSingleMetric = widget.metrics.length === 1
    const metricColumns = widget.metrics.map((metric, index): TDataTableColumn<TableRow> => {
      const column = reportColumnsMap.get(metric.kpi)
      return {
        accessor: String(index),
        title: getSelectedReportColumnDisplayLabel({
          value: metric.kpi,
          selectedColumns,
          columnsMap: reportColumnsMap,
          variantMap,
        }),
        grow: hasSingleMetric,
        ...(hasSingleMetric ? {} : { width: MULTI_METRIC_COLUMN_WIDTH }),
        render: (row: TableRow) =>
          column ? (
            <DisplayReportColumnValue columnDef={column} grouping={grouping} value={row.metrics[index]} />
          ) : (
            formatFallbackValue(row.metrics[index])
          ),
      }
    })
    return [
      {
        accessor: 'entity',
        title: 'Name',
        render: (row: TableRow) => getReportGroupEntityName(row.entity),
      },
      ...metricColumns,
    ]
  }, [widget.metrics, reportColumnsMap, grouping, selectedColumns, variantMap])

  if (isEmpty(records)) return <EmptyTableWidget />

  return (
    <div className='flex max-h-[440px] min-h-0 min-w-0 flex-col [&>*]:min-h-0 [&>*]:min-w-0'>
      <TDataTable
        key={`table-${widget.widgetId}`}
        columns={columns}
        idAccessor={(row) => getReportGroupEntityId(row.entity)}
        records={records}
      />
    </div>
  )
}

function formatFallbackValue(value: ReportColumnValue): string {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'number') return value.toLocaleString()
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') return '-'
  return value
}

function EmptyTableWidget(): ReactElement {
  return (
    <Group justify='center'>
      <Text c='dimmed' size='sm'>
        No data
      </Text>
    </Group>
  )
}
